/**
 * StoryRush Facebook / Meta analytics helper.
 *
 * Responsibilities:
 * 1. Initialize Meta Pixel and send browser Pixel events.
 * 2. Upload the same normalized event payload to StoryRush server `/Dataanalysis/dataup`.
 * 3. Generate and reuse `event_id`, so backend can later use the same event_id for CAPI deduplication.
 *
 * Do not put Facebook App Secret in frontend code. App Secret / CAPI Access Token must stay on server.
 */

import Api from '@/api'
import { myFetch } from '@/api/request'
import { useUserStore } from '@/stores/user'
import { v4 as uuidv4 } from 'uuid'

type Primitive = string | number | boolean
export type AnalyticsValue = Primitive | null | undefined
export type AnalyticsParams = Record<string, AnalyticsValue>

type PixelParams = Record<string, string | number | boolean | string[] | number[]>

type StoryRushEventName =
  | 'page_view'
  | 'content_click'
  | 'content_start'
  | 'paywall_view'
  | 'checkout_start'
  | 'purchase_success'
  | 'subscribe_success'
  | 'registration_success'
  | 'content_3_start'

type MetaEventConfig = {
  metaName: string
  method: 'track' | 'trackCustom'
}

type TrackOptions = {
  /** 是否等待服务器上传完成。跳转支付页前建议传 true。 */
  waitServer?: boolean
  /** 只上传服务器，不触发 Pixel。支付服务端回调类事件可按需使用。 */
  skipPixel?: boolean
  /** 只触发 Pixel，不上传服务器。默认 false。 */
  skipServer?: boolean
}

export type StoryRushEventPayload = Record<string, string | number | boolean>

declare global {
  interface Window {
    fbq?: (...args: any[]) => void
    _fbq?: (...args: any[]) => void
  }
}

const META_PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID || import.meta.env.VITE_FACEBOOK_PIXEL_ID || '2831947643815553'
const META_PIXEL_DISABLED = import.meta.env.VITE_META_PIXEL_DISABLED === 'true'
const META_PIXEL_CURRENCY = import.meta.env.VITE_META_PIXEL_CURRENCY || import.meta.env.VITE_META_CURRENCY || 'USD'
const APP_VERSION = import.meta.env.VITE_APP_VERSION || '1.0.0'
const PLATFORM = import.meta.env.VITE_ANALYTICS_PLATFORM || 'H5'
const SESSION_STORAGE_KEY = 'sr_analytics_session_id'
const ATTRIBUTION_STORAGE_KEY = 'sr_meta_attribution'
const FBC_COOKIE = '_fbc'
const FBP_COOKIE = '_fbp'

let initialized = false
let pageViewTracked = false

const META_EVENT_MAP: Record<StoryRushEventName, MetaEventConfig> = {
  page_view: { metaName: 'PageView', method: 'track' },
  content_click: { metaName: 'content_click', method: 'trackCustom' },
  content_start: { metaName: 'ViewContent', method: 'track' },
  paywall_view: { metaName: 'paywall_view', method: 'trackCustom' },
  checkout_start: { metaName: 'InitiateCheckout', method: 'track' },
  purchase_success: { metaName: 'Purchase', method: 'track' },
  subscribe_success: { metaName: 'Subscribe', method: 'track' },
  registration_success: { metaName: 'CompleteRegistration', method: 'track' },
  content_3_start: { metaName: 'content_3_start', method: 'trackCustom' },
}

/**
 * 服务器要求 data 中尽量带全字段：有值传值，没有值传空字符串。
 */
const ANALYTICS_KEYS = [
  'eventName',
  'event_time',
  'event_id',
  'user_id',
  'device_id',
  'session_id',
  'platform',
  'app_version',
  'language',
  'source',
  'page_url',
  'os_version',
  'referrer',
  'fbclid',
  'fbp',
  'fbc',
  'adgroup_id',
  'adplan_id',
  'adcreative_id',
  'protype',
  'djid',
  'content_title',
  'videoid',
  'page_name',
  'source_page',
  'is_login',
  'is_vip',
  'diamond_balance',
  'product_id',
  'product_type',
  'price',
  'value',
  'currency',
  'order_id',
  'transaction_id',
  'original_transaction_id',
  'payment_channel',
  'diamond_amount',
  'balance_after',
  'vip_duration',
  'subscription_group_id',
  'is_trial',
  'is_first_purchase',
  'is_first_subscribe',
  'storefront',
  'unlock_type',
  'unlock_status',
  'diamond_required',
  'vip_required',
  'locked_count',
  'auto_unlock_status',
  'fail_reason',
  'renewal_count',
  'recommend_position',
  'category',
  'read_mode',
  'font_size',
  'page_turn_mode',
  'progress_percent',
  'watch_seconds',
  'quality',
  'register_method',
  'ai_role_id',
  'ai_role_type',
  'message_id',
  'message_count',
  'free_quota_left',
  'feedback_type',
] as const

function canUseDOM() {
  return typeof window !== 'undefined' && typeof document !== 'undefined'
}

function safeString(value: AnalyticsValue): string | number | boolean {
  if (value === undefined || value === null) return ''
  return value
}

function cleanPixelParams(params: StoryRushEventPayload): PixelParams {
  const result: PixelParams = {}
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return
    result[key] = value as string | number | boolean
  })
  return result
}

function getCookie(name: string): string {
  if (!canUseDOM()) return ''
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : ''
}

function setCookie(name: string, value: string, days = 90) {
  if (!canUseDOM()) return
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString()
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`
}

function getUrlParam(name: string): string {
  if (!canUseDOM()) return ''
  return new URLSearchParams(window.location.search).get(name) || ''
}

function readStoredAttribution(): Record<string, string> {
  if (!canUseDOM()) return {}
  try {
    return JSON.parse(localStorage.getItem(ATTRIBUTION_STORAGE_KEY) || '{}') || {}
  } catch {
    return {}
  }
}

function writeStoredAttribution(data: Record<string, string>) {
  if (!canUseDOM()) return
  try {
    localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(data))
  } catch {
    // ignore storage errors
  }
}

function ensureFbp(): string {
  let fbp = getCookie(FBP_COOKIE)
  if (!fbp) {
    fbp = `fb.1.${Math.floor(Date.now() / 1000)}.${Math.floor(Math.random() * 10000000000)}`
    setCookie(FBP_COOKIE, fbp)
  }
  return fbp
}

function syncAttributionFromUrl(): Record<string, string> {
  if (!canUseDOM()) return readStoredAttribution()

  const stored = readStoredAttribution()
  const fbclid = getUrlParam('fbclid') || stored.fbclid || ''
  const utmSource = getUrlParam('utm_source') || stored.utm_source || ''
  const adgroupId = getUrlParam('adgroup_id') || stored.adgroup_id || ''
  const adplanId = getUrlParam('adplan_id') || stored.adplan_id || ''
  const adcreativeId = getUrlParam('adcreative_id') || stored.adcreative_id || ''

  const next: Record<string, string> = {
    ...stored,
    fbclid,
    utm_source: utmSource,
    utm_medium: getUrlParam('utm_medium') || stored.utm_medium || '',
    utm_campaign: getUrlParam('utm_campaign') || stored.utm_campaign || '',
    utm_content: getUrlParam('utm_content') || stored.utm_content || '',
    adgroup_id: adgroupId,
    adplan_id: adplanId,
    adcreative_id: adcreativeId,
  }

  if (fbclid) {
    const fbc = `fb.1.${Math.floor(Date.now() / 1000)}.${fbclid}`
    setCookie(FBC_COOKIE, fbc)
    next.fbc = fbc
  } else {
    next.fbc = getCookie(FBC_COOKIE) || stored.fbc || ''
  }

  const hasNewAttribution = Boolean(
    getUrlParam('fbclid') ||
    getUrlParam('utm_source') ||
    getUrlParam('adgroup_id') ||
    getUrlParam('adplan_id') ||
    getUrlParam('adcreative_id'),
  )

  if (hasNewAttribution) {
    writeStoredAttribution(next)
  }

  return next
}

function getSource(attribution: Record<string, string>) {
  const source = (attribution.utm_source || '').toLowerCase()
  if (attribution.fbclid || attribution.fbc || source.includes('facebook') || source.includes('meta')) {
    return 'facebook'
  }
  return 'organic'
}

function getSessionId() {
  if (!canUseDOM()) return ''
  let sessionId = sessionStorage.getItem(SESSION_STORAGE_KEY) || ''
  if (!sessionId) {
    sessionId = `s_${Date.now()}_${uuidv4().replace(/-/g, '')}`
    sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId)
  }
  return sessionId
}

function getDeviceId() {
  const userStore = useUserStore()
  let uuid = userStore.getUuid
  if (!uuid) {
    uuid = uuidv4()
    userStore.setUuid(uuid)
  }
  return uuid
}

function normalizeLanguage(language?: string) {
  if (language === 'zh-Hant' || language === 'tw' || language === 'zh_TW') return 'tw'
  if (language === 'zh-Hans' || language === 'cn' || language === 'zh_cn') return 'cn'
  if (language === 'en') return 'en'
  return language || 'tw'
}

function getOSVersion() {
  if (!canUseDOM()) return ''
  const ua = window.navigator.userAgent || ''
  const android = ua.match(/Android\s([\d.]+)/i)
  if (android?.[1]) return `Android ${android[1]}`

  const ios = ua.match(/OS\s([\d_]+)\slike\sMac\sOS\sX/i)
  if (ios?.[1]) return `iOS ${ios[1].replace(/_/g, '.')}`

  const mac = ua.match(/Mac OS X\s([\d_]+)/i)
  if (mac?.[1]) return `macOS ${mac[1].replace(/_/g, '.')}`

  const windows = ua.match(/Windows NT\s([\d.]+)/i)
  if (windows?.[1]) return `Windows NT ${windows[1]}`

  return ''
}

function buildEventId(eventName: string) {
  return `evt_${eventName}_${Date.now()}_${uuidv4().replace(/-/g, '').slice(0, 12)}`
}

function getCurrentPageName() {
  if (!canUseDOM()) return ''
  const path = window.location.pathname || '/'
  if (path === '/') return 'home'
  return path.replace(/^\//, '').split('/')[0] || 'home'
}

function buildCommonPayload(eventName: StoryRushEventName, params: AnalyticsParams = {}): StoryRushEventPayload {
  const userStore = useUserStore()
  const userInfo = userStore.getUserInfo
  const attribution = syncAttributionFromUrl()
  const currentUrl = canUseDOM() ? window.location.href : ''

  const base: StoryRushEventPayload = {}
  ANALYTICS_KEYS.forEach((key) => {
    base[key] = ''
  })

  const payload: StoryRushEventPayload = {
    ...base,
    eventName: eventName,
    event_time: Date.now(),
    event_id: buildEventId(eventName),
    user_id: userInfo.id || getDeviceId(),
    device_id: getDeviceId(),
    session_id: getSessionId(),
    platform: PLATFORM,
    app_version: APP_VERSION,
    language: normalizeLanguage(userInfo.language),
    source: getSource(attribution),
    page_url: currentUrl,
    os_version: getOSVersion(),
    referrer: canUseDOM() ? document.referrer || '' : '',
    fbclid: attribution.fbclid || '',
    fbp: ensureFbp(),
    fbc: getCookie(FBC_COOKIE) || attribution.fbc || '',
    adgroup_id: attribution.adgroup_id || '',
    adplan_id: attribution.adplan_id || '',
    adcreative_id: attribution.adcreative_id || '',
    page_name: getCurrentPageName(),
    is_login: Boolean(userStore.isRealLogin),
    is_vip: Number(userInfo.isvip || 0) === 1,
    diamond_balance: Number(userInfo.coin || 0),
    currency: META_PIXEL_CURRENCY,
  }

  Object.entries(params).forEach(([key, value]) => {
    payload[key] = safeString(value)
  })

  return payload
}

function buildPixelParams(payload: StoryRushEventPayload): PixelParams {
  const params: PixelParams = cleanPixelParams(payload)

  if (payload.djid) {
    params.content_ids = [String(payload.videoid || payload.djid)]
    params.content_category = String(payload.protype || '')
  }

  if (payload.content_title) {
    params.content_name = String(payload.content_title)
  }

  if (payload.product_id) {
    params.content_ids = [String(payload.product_id)]
    params.content_type = String(payload.product_type || 'product')
  }

  if (payload.value || payload.price) {
    params.value = payload.value || payload.price
  }

  params.currency = String(payload.currency || META_PIXEL_CURRENCY)

  return params
}

function initFbqBase() {
  if (!window.fbq) {
    const fbq: any = function (...args: any[]) {
      fbq.callMethod ? fbq.callMethod(...args) : fbq.queue.push(args)
    }
    fbq.push = fbq
    fbq.loaded = true
    fbq.version = '2.0'
    fbq.queue = []
    window.fbq = fbq
    window._fbq = fbq
  }
}

export function getMetaPixelId() {
  return META_PIXEL_ID
}

export function isMetaPixelEnabled() {
  return Boolean(META_PIXEL_ID && !META_PIXEL_DISABLED && canUseDOM())
}

export function initMetaPixel() {
  if (!isMetaPixelEnabled()) return false
  if (initialized) return true

  initFbqBase()

  if (!document.getElementById('meta-pixel-sdk')) {
    const script = document.createElement('script')
    script.id = 'meta-pixel-sdk'
    script.async = true
    script.src = 'https://connect.facebook.net/en_US/fbevents.js'
    document.head.appendChild(script)
  }

  window.fbq?.('init', META_PIXEL_ID)
  initialized = true
  return true
}

function sendToPixel(eventName: StoryRushEventName, payload: StoryRushEventPayload) {
  const config = META_EVENT_MAP[eventName]
  if (!config || !initMetaPixel()) return

  const params = buildPixelParams(payload)
  const eventOptions = payload.event_id ? { eventID: String(payload.event_id) } : undefined

  if (config.method === 'trackCustom') {
    window.fbq?.('trackCustom', config.metaName, params, eventOptions)
  } else {
    window.fbq?.('track', config.metaName, params, eventOptions)
  }
}

async function uploadToStoryRushServer(payload: StoryRushEventPayload) {
  try {
    await myFetch(
      Api.dataUp,
      {
        data: JSON.stringify(payload),
      },
      {
        throwOnBusinessError: false,
      },
    )
  } catch (err) {
    console.warn('StoryRush 埋点上传失败：', payload.eventName, err)
  }
}

export async function trackStoryRushEvent(
  eventName: StoryRushEventName,
  params: AnalyticsParams = {},
  options: TrackOptions = {},
) {
  const payload = buildCommonPayload(eventName, params)

  if (!options.skipPixel) {
    sendToPixel(eventName, payload)
  }

  if (!options.skipServer) {
    const uploadPromise = uploadToStoryRushServer(payload)
    if (options.waitServer) {
      await uploadPromise
    }
  }

  return payload
}

export function trackMetaPageView(extraParams: AnalyticsParams = {}) {
  pageViewTracked = true
  void trackStoryRushEvent('page_view', extraParams)
}

export function trackInitialMetaPageView() {
  if (pageViewTracked) return
  trackMetaPageView()
}

export function trackContentClick(params: AnalyticsParams) {
  void trackStoryRushEvent('content_click', params)
}

export function trackContentStart(params: AnalyticsParams) {
  void trackStoryRushEvent('content_start', params)
}

export function trackContent3Start(params: AnalyticsParams) {
  void trackStoryRushEvent('content_3_start', params)
}

export function trackPaywallView(params: AnalyticsParams) {
  void trackStoryRushEvent('paywall_view', params)
}

export function trackCheckoutStart(params: AnalyticsParams, options: TrackOptions = {}) {
  return trackStoryRushEvent('checkout_start', params, options)
}

export function trackPurchaseSuccess(params: AnalyticsParams, options: TrackOptions = {}) {
  return trackStoryRushEvent('purchase_success', params, options)
}

export function trackSubscribeSuccess(params: AnalyticsParams, options: TrackOptions = {}) {
  return trackStoryRushEvent('subscribe_success', params, options)
}

export function trackMetaCompleteRegistration(params: AnalyticsParams = {}) {
  void trackStoryRushEvent('registration_success', {
    register_method: params.method || params.register_method || '',
    ...params,
  })
}

/**
 * 兼容旧调用。现在广告来源通过公共参数 fbclid/utm/adgroup/adplan/adcreative 进入每个事件，
 * 不再额外上报 StoryRushAdLanding 这类需求表外事件。
 */
export function trackMetaAdLandingOnce() {
  syncAttributionFromUrl()
}
